import { useCallback, useEffect, useState } from "react";
import { Eye, Pencil, Plus, PlusCircle, Trash2, AlertCircle, RefreshCw, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { addBox, addSlots, deleteBoxes, deleteSlots, getBoxbyId, getSlotByDate, updateBox, updateSlot } from "../service/api";
import { toast } from "sonner";
import { format, isValid, parseISO } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DatePicker } from "../components/ui/date-picker";
import { TimePicker } from "../components/ui/time-picker";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const Boxes = () => {
    const location = useLocation();
    const id = location.state.id;
    const [boxes, setBoxes] = useState([]);
    const [mode, setMode] = useState('add');
    const [slotMode, setSlotMode] = useState('add');
    const [addModal, setAddModal] = useState(false);
    const [slotAddModal, setSlotAddModal] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [editBoxId, setEditBoxId] = useState<any>();
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteSlotModal, setDeleteSlotModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [boxId, setBoxId] = useState<any>();
    const [slotId, setSlotId] = useState<any>();
    const [viewSlotRows, setViewSlotRows] = useState<any[]>([]);
    const [loadingViewSlots, setLoadingViewSlots] = useState(false);
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [formValues, setFormValues] = useState({ name: '', type: '', is_active: false });
    const [slotFormValues, setSlotFormValues] = useState({
        date: '', from_time: '', to_time: '', price: '', is_available: false, reason: ''
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getBoxes();
    }, []);

    const fetchSlotsForView = useCallback(async () => {
        if (!viewModal || boxId == null || boxId === undefined) return;
        setLoadingViewSlots(true);
        try {
            const res = await getSlotByDate({ box_id: boxId as number, date: selectedDate });
            const list = res?.data?.data;
            setViewSlotRows(Array.isArray(list) ? list : []);
        } catch {
            setViewSlotRows([]);
        } finally {
            setLoadingViewSlots(false);
        }
    }, [viewModal, boxId, selectedDate]);

    useEffect(() => {
        fetchSlotsForView();
    }, [fetchSlotsForView]);

    const getBoxes = async () => {
        try {
            setLoading(true);
            let res = await getBoxbyId(id);
            setBoxes(res.data.data);
        } catch {
            // boxes stay empty
        } finally {
            setLoading(false);
        }
    };

    const openModal = (item: any, data: any) => {
        if (data === 'update') {
            setFormValues({ name: item.name, type: item.type, is_active: item.is_active === 1 });
            setMode('update');
            setEditBoxId(item.id);
        } else {
            setFormValues({ name: '', type: '', is_active: false });
            setMode('add');
        }
        setAddModal(true);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formValues.name.trim()) newErrors.name = "Name is required";
        if (!formValues.type.trim()) newErrors.type = "Type is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const slotValidateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!slotFormValues.date) newErrors.date = "Date is required";
        if (!slotFormValues.from_time) newErrors.from_time = "From time is required";
        if (!slotFormValues.to_time) newErrors.to_time = "To time is required";
        if (!slotFormValues.price) newErrors.price = "Price is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;
        try {
            const body = { name: formValues.name, type: formValues.type, is_active: formValues.is_active, partnerId: id };
            if (mode === 'add') {
                await addBox(body);
                toast.success("Turf added");
            } else {
                await updateBox({ ...body, id: editBoxId });
                toast.success("Turf updated");
            }
            getBoxes();
            setAddModal(false);
        } catch {
            toast.error("Failed to save turf");
        }
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleSlotInputChange = (e: any) => {
        const { name, value } = e.target;
        setSlotFormValues({ ...slotFormValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleSlotAvailableChange = (checked: boolean) => {
        setSlotFormValues((prevState) => ({
            ...prevState,
            is_available: checked,
            reason: checked ? '' : prevState.reason
        }));
    };

    const handleDelete = (item: any) => {
        setDeleteModal(true);
        setEditBoxId(item.id);
    };

    const deleteBox = async () => {
        try {
            await deleteBoxes({ id: editBoxId });
            toast.success("Turf deleted");
            setDeleteModal(false);
            getBoxes();
        } catch {
            toast.error("Failed to delete turf");
        }
    };

    const handleView = (item: any) => {
        setBoxId(item.id);
        setViewModal(true);
    };

    const handleViewDateChange = (date: Date | undefined) => {
        if (date && isValid(date)) setSelectedDate(format(date, 'yyyy-MM-dd'));
    };

    const slotsAddModal = (data: any, type: any) => {
        if (type === 'update' && (data.id == null || data.id === undefined)) return;
        if (type === 'update') {
            setSlotFormValues({
                date: data.date,
                from_time: data.from_time,
                to_time: data.to_time,
                reason: data.not_available_reason,
                price: data.price,
                is_available: data.is_available === 1 ? true : false,
            });
            setSlotMode('update');
            setSlotId(data.id);
        } else {
            setSlotFormValues({ date: '', from_time: '', to_time: '', reason: '', price: '', is_available: false });
            setBoxId(data.id);
            setSlotMode('add');
            setSlotId(data.id);
        }
        setSlotAddModal(true);
    };

    const handleSubmitSlots = async (event: any) => {
        event.preventDefault();
        if (!slotValidateForm()) return;
        try {
            const body = {
                date: slotFormValues.date,
                from_time: slotFormValues.from_time,
                to_time: slotFormValues.to_time,
                price: slotFormValues.price,
                is_available: slotFormValues.is_available,
                not_available_reason: slotFormValues.reason,
                box_id: boxId
            };
            if (slotMode === 'add') {
                await addSlots(body);
                toast.success("Slot added");
                setSlotAddModal(false);
                await fetchSlotsForView();
            } else {
                await updateSlot({ ...body, id: slotId });
                toast.success("Slot updated");
                setSlotAddModal(false);
                setViewModal(false);
            }
        } catch {
            toast.error("Failed to save slot");
        }
    };

    const slotsDeleteModal = (data: any) => {
        if (data?.id == null) return;
        setSlotId(data.id);
        setViewModal(false);
        setDeleteSlotModal(true);
    };

    const deleteSlot = async () => {
        try {
            await deleteSlots({ id: slotId });
            toast.success("Slot deleted");
            setDeleteSlotModal(false);
        } catch {
            toast.error("Failed to delete slot");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Turfs (boxes)</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Turfs for this partner. Add slots per turf and date, or manage from the Partners screen.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getBoxes()}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={() => openModal([], "add")}>
                        <Plus className="h-4 w-4 mr-1" /> Add turf
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b">
                    <h2 className="text-base font-semibold">All turfs</h2>
                </div>
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right min-w-[12rem]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {boxes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-14">
                                            No turfs yet. Add one to start scheduling.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    boxes?.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell className="font-medium">{item.name}</TableCell>
                                            <TableCell>{item.type}</TableCell>
                                            <TableCell>
                                                <Badge variant={item.is_active === 1 ? "default" : "secondary"}>
                                                    {item.is_active === 1 ? "Active" : "Inactive"}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex flex-wrap justify-end gap-1">
                                                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => slotsAddModal(item, "add")}>
                                                        <PlusCircle className="h-3.5 w-3.5 text-primary" /> Slot
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openModal(item, "update")}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleView(item)}>
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDelete(item)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Add/Edit box dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{mode === 'add' ? 'Add turf' : 'Edit turf'}</DialogTitle>
                        <DialogDescription>Fill in the turf details and set its status.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Name</Label>
                            <Input name="name" placeholder="Enter name" value={formValues.name} onChange={handleInputChange} />
                            {errors.name && <p className="text-destructive text-xs">{errors.name}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Type</Label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                value={formValues.type}
                                onChange={(e) => setFormValues({ ...formValues, type: e.target.value })}
                            >
                                <option value="">Select sport</option>
                                <option>Cricket</option>
                                <option>Basketball</option>
                                <option>Wollyball</option>
                            </select>
                            {errors.type && <p className="text-destructive text-xs">{errors.type}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="box-active"
                                checked={formValues.is_active}
                                onCheckedChange={(checked) => setFormValues({ ...formValues, is_active: checked })}
                            />
                            <Label htmlFor="box-active" className="cursor-pointer">Active</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>{mode === 'add' ? 'Add turf' : 'Update turf'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete box dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this turf?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteBox}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View slots dialog */}
            <Dialog open={viewModal} onOpenChange={setViewModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>View turf slots</DialogTitle>
                        <DialogDescription>Slots for the selected date. Green = available, yellow = booked, red = not available.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <Label>Date</Label>
                            <DatePicker
                                value={selectedDate ? parseISO(selectedDate) : undefined}
                                onChange={handleViewDateChange}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-green-300 border" />Available</span>
                            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-yellow-200 border" />Already booked</span>
                            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-red-200 border" />Not available</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Uses <code className="text-[10px] bg-muted px-1 rounded">get_slot_by_date</code>: materialized rows or resolved weekly/platform slots (no row id = view-only).
                        </p>
                        {loadingViewSlots ? (
                            <div className="flex justify-center py-12">
                                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
                                {viewSlotRows.length === 0 ? (
                                    <p className="col-span-full text-center text-sm text-muted-foreground py-6">No slots for this date.</p>
                                ) : (
                                    viewSlotRows.map((slot: any, index: number) => {
                                        const isResolved = slot.id == null || slot.resolved_from === "turf_availability";
                                        let bgColor = "";
                                        let borderColor = "";
                                        if (slot.is_available) {
                                            bgColor = "bg-green-100";
                                            borderColor = "border-green-300";
                                        } else if (!slot.is_available && slot.not_available_reason === 0) {
                                            bgColor = "bg-yellow-100";
                                            borderColor = "border-yellow-300";
                                        } else if (!slot.is_available && slot.not_available_reason === 1) {
                                            bgColor = "bg-red-100";
                                            borderColor = "border-red-300";
                                        }
                                        return (
                                            <div
                                                key={slot.id != null ? slot.id : `r-${index}-${slot.from_time}`}
                                                className={`rounded-xl border ${borderColor} ${bgColor} p-2`}
                                            >
                                                <div className="flex items-center justify-between mb-1">
                                                    <p className="text-xs font-medium">
                                                        ₹{slot.price}
                                                        {isResolved && <span className="block text-[10px] text-muted-foreground">(resolved)</span>}
                                                    </p>
                                                    {!isResolved && (
                                                        <div className="flex gap-1">
                                                            <button onClick={() => slotsAddModal(slot, "update")} className="p-0.5">
                                                                <Pencil className="h-3 w-3 text-blue-600" />
                                                            </button>
                                                            <button onClick={() => slotsDeleteModal(slot)} className="p-0.5">
                                                                <Trash2 className="h-3 w-3 text-red-600" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-xs">{slot.from_time} – {slot.to_time}</p>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add/Edit slot dialog */}
            <Dialog open={slotAddModal} onOpenChange={setSlotAddModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{slotMode === 'add' ? 'Add slot' : 'Update slot'}</DialogTitle>
                        <DialogDescription>Set the date, time window, price, and availability for this slot.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Date</Label>
                            <DatePicker
                                value={slotFormValues.date ? parseISO(slotFormValues.date) : undefined}
                                onChange={(date) => {
                                    setSlotFormValues({ ...slotFormValues, date: date ? format(date, 'yyyy-MM-dd') : '' });
                                    setErrors({ ...errors, date: "" });
                                }}
                                className="w-full"
                            />
                            {errors.date && <p className="text-destructive text-xs">{errors.date}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>From time</Label>
                            <TimePicker
                                value={slotFormValues.from_time || ''}
                                onChange={(v) => {
                                    setSlotFormValues({ ...slotFormValues, from_time: v });
                                    setErrors({ ...errors, from_time: "" });
                                }}
                                className="w-full"
                            />
                            {errors.from_time && <p className="text-destructive text-xs">{errors.from_time}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>To time</Label>
                            <TimePicker
                                value={slotFormValues.to_time || ''}
                                onChange={(v) => {
                                    setSlotFormValues({ ...slotFormValues, to_time: v });
                                    setErrors({ ...errors, to_time: "" });
                                }}
                                className="w-full"
                            />
                            {errors.to_time && <p className="text-destructive text-xs">{errors.to_time}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Price</Label>
                            <Input type="text" name="price" placeholder="Enter price" value={slotFormValues.price} onChange={handleSlotInputChange} />
                            {errors.price && <p className="text-destructive text-xs">{errors.price}</p>}
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch
                                id="slot-available"
                                checked={slotFormValues.is_available}
                                onCheckedChange={handleSlotAvailableChange}
                            />
                            <Label htmlFor="slot-available" className="cursor-pointer">Available</Label>
                        </div>
                        {!slotFormValues.is_available && (
                            <div className="space-y-1.5">
                                <Label>Reason for unavailability</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    value={slotFormValues.reason}
                                    onChange={(e) => setSlotFormValues({ ...slotFormValues, reason: e.target.value })}
                                >
                                    <option value="">Select reason</option>
                                    <option value="0">Already booked</option>
                                    <option value="1">Partner/box not available</option>
                                </select>
                                {errors.reason && <p className="text-destructive text-xs">{errors.reason}</p>}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSlotAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmitSlots}>{slotMode === 'add' ? 'Add slot' : 'Update slot'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete slot dialog */}
            <Dialog open={deleteSlotModal} onOpenChange={setDeleteSlotModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this slot?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteSlotModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteSlot}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Boxes;
